import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";

import {
  useCaseImages,
  useLibraryCaseImages,
  type CaseImage,
  type TestCase,
} from "@/api/tests-api";
import { atWindow, buildSeries, windowsOf } from "@/lib/imageStack";
import { init, setTextPrompt, TOOL_GROUP_ID } from "@/lib/viewer/cornerstone";
import { inSliceOrder, mprVerdict } from "@/lib/viewer/mpr";
import {
  ICON,
  MEASURE,
  NAVIGATE,
  SLABS,
  actionForKey,
  cellCount,
  describeTags,
  fillCells,
  cineInterval,
  cineTarget,
  imageCorners,
  instanceCount,
  isUltrasound,
  linksTo,
  nextCineIndex,
  pairRatios,
  planeCut,
  planeNormal,
  stackEntries,
  US_MEASURE,
  US_TOOLS,
  syncIndex,
  planeColourFor,
  toolById,
  type CellGeometry,
  type Link,
  type Measurement,
  type RoomLayout,
  type RoomTool,
  type RoomView,
  type Slab,
  type TagRow,
  type TagSource,
} from "@/lib/viewer/room";
import { roomPresetsFor } from "@/lib/viewer/windows";
import { TestCaseViewer, type CaseImageSource, type SeriesRailState } from "@/components/tests/TestCaseViewer";
import { RoomCell, type CellLine } from "./RoomCell";
import { releaseRoomVolumes, RoomVolume, type PlaneState } from "./RoomVolume";
import {
  EMPTY_VIEWPORT,
  RoomViewport,
  type RoomEngine,
  type ViewportHandle,
  type ViewportState,
} from "./RoomViewport";

import "@/styles/reading-room.css";

/**
 * The reading room -- docs/scope-reading-room.md, phases 1 to 3.
 *
 * The design's full-screen workstation (docs/design/PACS Viewer
 * Redesign-31.html) built over the existing diagnostic engine. What it
 * deliberately is NOT is in the scope's section 2: no patient identity
 * (this system holds none), no report or worksheet (the dictation is the
 * answer), no priors (a case has none). The banner is the CASE.
 *
 * It renders into `document.body` itself. A full-screen layer inside a
 * transformed or Radix-managed ancestor is either the size of the modal
 * or unclickable -- both measured here before -- so it never lives in
 * one; the caller closes its dialog before opening the room.
 */

// What the banner shows. A library case has all of it; the reviewer's
// payload carries the case context but no library status, and the
// "Published" pill means nothing to someone scoring a sitting.
type CaseInfo = Pick<TestCase, "title" | "modality" | "clinical_history"> &
  Partial<Pick<TestCase, "body_part" | "patient_sex" | "patient_age" | "status">>;

/** Moving between the cases of one sitting, for the reviewer. */
export interface CaseNav {
  position: number;
  count: number;
  onPrev: () => void;
  onNext: () => void;
}

/** Which of the full room's side columns are folded to a strip. */
export interface RoomFolds {
  dock: boolean;
  rail: boolean;
  panel: boolean;
}

export const NO_FOLDS: RoomFolds = { dock: false, rail: false, panel: false };

/** A folded column: the way back, its name, and whatever the host wants
 *  kept in sight (a clock, a recording light). */
function FoldStrip({
  id,
  label,
  side,
  onOpen,
  children,
}: {
  id: keyof RoomFolds;
  label: string;
  side: "left" | "right";
  onOpen: () => void;
  children?: ReactNode;
}) {
  return (
    <>
      <button
        type="button"
        className="rr-strip-btn"
        data-cy={`rr-unfold-${id}`}
        onClick={onOpen}
        title={`Show ${label}`}
        aria-label={`Show ${label}`}
      >
        <Icon d={side === "left" ? ICON.nextI : ICON.prevI} size={16} />
      </button>
      <span className="rr-strip-label">{label}</span>
      {children}
    </>
  );
}

function FoldButton({
  id,
  label,
  side,
  onFold,
}: {
  id: keyof RoomFolds;
  label: string;
  side: "left" | "right";
  onFold: () => void;
}) {
  return (
    <button
      type="button"
      className="rr-strip-btn"
      data-cy={`rr-fold-${id}`}
      onClick={onFold}
      title={`Fold ${label}`}
      aria-label={`Fold ${label}`}
    >
      <Icon d={side === "left" ? ICON.prevI : ICON.nextI} size={16} />
    </button>
  );
}

function Icon({ d, size = 20, fill = false }: { d: string; size?: number; fill?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill ? "currentColor" : "none"}
      stroke={fill ? "none" : "currentColor"}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

const LINK_NOTE: Record<Link, string | null> = {
  active: null,
  linked: "linked",
  "other-frame": "not linked · other frame of reference",
  "other-plane": "not linked · other plane",
  "no-positions": "not linked · no stored slice positions",
  empty: null,
};

let roomSeq = 0;

export function ReadingRoom({
  source,
  testCase,
  onClose,
  caseNav,
  extraTab,
  closeLabel = "Close",
  mode = "full",
  showSeriesRail = true,
  explainFallback = true,
  onSeries,
  onFullScreen,
  dockSide,
  onDock,
  dockLabel = "Your controls",
  dockStrip,
  dockBar,
  onDockBar,
  folds,
  onFoldsChange,
}: {
  source: CaseImageSource;
  testCase: CaseInfo;
  onClose: () => void;
  /** Previous / next case in the banner, when the room is one of several. */
  caseNav?: CaseNav;
  /** A first panel tab of the caller's own -- the reviewer's dictation. */
  extraTab?: { label: string; content: ReactNode };
  closeLabel?: string;
  /**
   * FULL is the room over everything. EMBEDDED is its viewer -- toolbar,
   * cells, slider, footer -- inside a host page's own pane, without the
   * banner and panel the page already has. The same component either way,
   * and switching does not remount it: see the host element below.
   */
  mode?: "full" | "embedded";
  /** Embedded only: draw the room's own series list. False where the page
   *  lists series in a pane of its own and drives it through `onSeries`. */
  showSeriesRail?: boolean;
  /** Say why the rendered images are showing. STAFF ONLY -- the note asks
   *  for a re-upload, which a candidate under a clock cannot do. */
  explainFallback?: boolean;
  onSeries?: (state: SeriesRailState) => void;
  /** Embedded: the toolbar's "Full screen" button. */
  onFullScreen?: () => void;
  /** Full: a column beside the images for the host page's own controls. */
  dockSide?: "left" | "right";
  onDock?: (element: HTMLElement | null) => void;
  /** The dock's heading, and what its folded strip keeps in sight. */
  dockLabel?: string;
  dockStrip?: ReactNode;
  /**
   * WITH THE DOCK FOLDED, what must stay in reach goes in a bar across
   * the top of the images: the host either renders `dockBar` there, or
   * takes the element from `onDockBar` and MOVES its own controls into it
   * (a recorder must be moved, never drawn twice). The element exists
   * only while the dock is folded.
   */
  dockBar?: ReactNode;
  onDockBar?: (element: HTMLElement | null) => void;
  /** Held by the host so a fold survives moving to the next case, which
   *  is a fresh room. Uncontrolled when absent. */
  folds?: RoomFolds;
  onFoldsChange?: (folds: RoomFolds) => void;
}) {
  const attemptImages = useCaseImages(
    source.kind === "attempt" ? source.attemptId : "",
    source.kind === "attempt" ? source.assignmentCaseId : null,
  );
  const libraryImages = useLibraryCaseImages(source.kind === "library" ? source.caseId : "");
  const query = source.kind === "attempt" ? attemptImages : libraryImages;
  const images = useMemo<CaseImage[]>(() => query.data ?? [], [query.data]);

  // One row per instance -- several windows of one slice are not slices.
  const instances = useMemo(() => {
    const windows = windowsOf(images);
    return windows.length ? atWindow(images, windows[0]!) : images;
  }, [images]);
  const series = useMemo(() => buildSeries(instances), [instances]);

  // Each series in SLICE order, as the pictures the viewer steps through:
  // one per still or slice, and every frame of a clip, played from its
  // stored DICOM (`stackEntries`). `frames` is the row behind each entry.
  const stacks = useMemo(
    () =>
      series.map((s) => {
        const ordered = inSliceOrder(s.images);
        const { entries, complete } = stackEntries(ordered);
        const fallback: null | "empty" | "not-converted" =
          ordered.length === 0 ? "empty" : complete ? null : "not-converted";
        return {
          entries,
          frames: entries.map((e) => e.row),
          imageIds: entries.map((e) => e.imageId),
          instances: instanceCount(entries),
          fallback,
        };
      }),
    [series],
  );

  const [layout, setLayout] = useState<RoomLayout>("1x1");
  const [view, setView] = useState<RoomView>("stack");
  const [cells, setCells] = useState<(number | null)[]>([0]);
  const [activeCell, setActiveCell] = useState(0);
  const [cellStates, setCellStates] = useState<ViewportState[]>([EMPTY_VIEWPORT]);
  const [plane, setPlane] = useState<PlaneState | null>(null);
  // The design opens with both on.
  const [refLines, setRefLines] = useState(true);
  const [sync, setSync] = useState(true);
  const [slab, setSlab] = useState<Slab>(5);
  const [tool, setTool] = useState<RoomTool>("wl");
  const [overlays, setOverlays] = useState(true);
  const [tab, setTab] = useState<"extra" | "meas" | "case" | "tags">(extraTab ? "extra" : "meas");
  const [drawn, setMeasurements] = useState<Measurement[]>([]);
  const measurements = useMemo(() => pairRatios(drawn), [drawn]);
  const [asking, setAsking] = useState<{ initial: string; done: (text: string | null) => void } | null>(null);
  const [tags, setTags] = useState<TagRow[] | null>(null);
  const [broken, setBroken] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const handles = useRef<(ViewportHandle | null)[]>([]);
  const volumeHandle = useRef<ViewportHandle | null>(null);
  const [nonce] = useState(() => ++roomSeq);

  const count = cellCount(layout);
  const readyCells = cellStates.filter((s) => s.ready).length;
  const activeSeries = cells[activeCell] ?? null;
  const active = activeSeries !== null ? series[activeSeries] : undefined;
  const stack = activeSeries !== null ? stacks[activeSeries] : undefined;
  const frames = useMemo(() => stack?.frames ?? [], [stack]);
  const imageIds = useMemo(() => stack?.imageIds ?? [], [stack]);

  // Every diagnostic image in the case, in series then slice order, so a
  // measurement can be found again from the panel wherever it was drawn.
  const placement = useMemo(() => {
    const map = new Map<string, { series: number; index: number }>();
    stacks.forEach((s, si) => s.imageIds.forEach((id, i) => map.set(id, { series: si, index: i })));
    return map;
  }, [stacks]);
  const caseImageIds = useMemo(() => [...placement.keys()], [placement]);
  const mpr = useMemo(() => mprVerdict(frames), [frames]);

  const activeLive = !broken && stack?.fallback === null;
  const anyLive = stacks.some((s) => s.fallback === null);
  // The old viewer takes the whole stage only when there is nothing to
  // read live at all, or a lone cell holds a series that cannot be: with
  // several cells, one JPEG-only series says so in its own cell.
  const stageFallback = !!broken || !anyLive || (count === 1 && !!stack && stack.fallback !== null);
  const live = activeLive;

  const modality = cellStates[activeCell]?.modality ?? testCase.modality ?? null;
  // THE ULTRASOUND SCREEN IS ITS OWN TOOLBAR, as the design draws it: no
  // stack scroll (a clip plays, it is not scrolled through), no HU probe
  // (there are no units to read), no window presets and no MPR.
  const ultrasound = isUltrasound(modality);
  const presets = roomPresetsFor(modality);
  const presetFor = (voi: { width: number; centre: number } | null) =>
    presets.find((p) => voi && p.width === voi.width && p.centre === voi.centre);

  const volumeReason = !activeLive
    ? activeSeries === null
      ? "pick a series for this cell first"
      : "this series holds only rendered images"
    : mpr.ok
      ? null
      : mpr.reason;
  const volume = view !== "stack" && !volumeReason;
  const crosshair = volume && refLines && layout !== "1x1";

  const vs: ViewportState = volume
    ? {
        ...EMPTY_VIEWPORT,
        ready: !!plane?.ready,
        index: plane?.index ?? 0,
        voi: plane?.voi ?? null,
        inverted: !!plane?.inverted,
        modality,
      }
    : (cellStates[activeCell] ?? EMPTY_VIEWPORT);
  const total = volume ? (plane?.total ?? 0) : imageIds.length;
  const matchedPreset = presetFor(vs.voi);
  const diagnosticCount = images.filter((i) => i.dicom_url).length;

  const current = useCallback(
    () => (volume ? volumeHandle.current : (handles.current[activeCell] ?? null)),
    [volume, activeCell],
  );

  // One stable setter per cell. A fresh callback each render would re-run
  // the viewport's report effect every render, and that effect sets this
  // state -- a loop.
  const cellSetters = useMemo(
    () =>
      [0, 1, 2, 3].map((i) => (s: ViewportState) =>
        setCellStates((prev) => {
          if (prev[i] === s) return prev;
          const next = prev.slice();
          next[i] = s;
          return next;
        }),
      ),
    [],
  );

  // ------------------------------------------------------------------
  // The room's one rendering engine, which every stack cell joins.
  // ------------------------------------------------------------------
  const enginePromise = useRef<Promise<RoomEngine> | null>(null);
  const roomEngine = useRef<RoomEngine | null>(null);
  const getEngine = useCallback(() => {
    enginePromise.current ??= init().then((cs) => {
      const engine = new cs.core.RenderingEngine(`rr-re-${nonce}`) as unknown as RoomEngine["engine"];
      roomEngine.current = { cs, engine };
      return roomEngine.current;
    });
    return enginePromise.current;
  }, [nonce]);
  // And one for the volume views, for the same reasons: a layout change
  // must not cost a new WebGL context, nor strand the cached volume's
  // texture in a context nobody draws with any more.
  const volumeEnginePromise = useRef<Promise<RoomEngine> | null>(null);
  const volumeEngine = useRef<RoomEngine | null>(null);
  const getVolumeEngine = useCallback(() => {
    volumeEnginePromise.current ??= init().then((cs) => {
      const engine = new cs.core.RenderingEngine(`rr-vol-re-${nonce}`) as unknown as RoomEngine["engine"];
      volumeEngine.current = { cs, engine };
      return volumeEngine.current;
    });
    return volumeEnginePromise.current;
  }, [nonce]);

  // Leaving: hand the shared group back the way the existing viewer
  // expects it (pan on the left button), and let go
  // of the engine and every volume built. A CELL must
  // not do the first of these -- the first cell to unmount would take
  // the reader's tool away from the cells still on screen.
  useEffect(
    () => () => {
      void init()
        .then((cs) => {
          const group = cs.tools.ToolGroupManager.getToolGroup(TOOL_GROUP_ID);
          if (group) {
            for (const t of [...NAVIGATE, ...MEASURE]) if (t.cs !== "Pan") group.setToolPassive(t.cs);
            group.setToolActive("Pan", { bindings: [{ mouseButton: cs.tools.Enums.MouseBindings.Primary }] });
          }
          releaseRoomVolumes(cs);
          for (const room of [roomEngine.current, volumeEngine.current]) {
            try {
              room?.engine.destroy();
            } catch {
              /* already gone */
            }
          }
        })
        .catch(() => undefined);
    },
    [],
  );

  // Measurements are for READING: nothing persists them, so they leave
  // with the room rather than reappearing in the next viewer to open
  // these images.
  useEffect(
    () => () => {
      void init().then((cs) => {
        const ours = new Set(caseImageIds);
        const all = cs.tools.annotation.state.getAllAnnotations() as {
          annotationUID?: string;
          metadata?: { referencedImageId?: string };
        }[];
        for (const a of all) {
          if (a.annotationUID && a.metadata?.referencedImageId && ours.has(a.metadata.referencedImageId)) {
            cs.tools.annotation.state.removeAnnotation(a.annotationUID);
          }
        }
      }).catch(() => undefined);
    },
    [caseImageIds],
  );

  // The text label asks for its words here rather than through
  // `window.prompt` -- see `setTextPrompt`. A question still open when the
  // room closes is answered "none", which removes the half-made label.
  const askingRef = useRef(asking);
  askingRef.current = asking;
  useEffect(() => {
    setTextPrompt((initial, done) => setAsking({ initial, done }));
    return () => {
      setTextPrompt(null);
      askingRef.current?.done(null);
    };
  }, []);
  const answer = (text: string | null) => {
    asking?.done(text);
    setAsking(null);
  };

  // ------------------------------------------------------------------
  // Layout, series and view changes.
  // ------------------------------------------------------------------
  const changeLayout = useCallback(
    (next: RoomLayout) => {
      const n = cellCount(next);
      setPlaying(false);
      setLayout(next);
      setCells((prev) => fillCells(prev, next, series.length));
      setCellStates((prev) => Array.from({ length: n }, (_, i) => prev[i] ?? EMPTY_VIEWPORT));
      setActiveCell((a) => Math.min(a, n - 1));
    },
    [series.length],
  );

  const loadSeries = useCallback(
    (i: number) => {
      if (i < 0 || i >= series.length || cells[activeCell] === i) return;
      setPlaying(false);
      setCells((prev) => prev.map((c, j) => (j === activeCell ? i : c)));
      setCellStates((prev) => prev.map((s, j) => (j === activeCell ? EMPTY_VIEWPORT : s)));
      const next = stacks[i];
      if (view !== "stack" && (!next || next.fallback !== null || !mprVerdict(next.frames).ok)) setView("stack");
    },
    [series.length, cells, activeCell, stacks, view],
  );

  const toggleMpr = () => {
    if (volumeReason) return;
    setPlaying(false);
    if (view === "mpr") {
      setView("stack");
    } else {
      setView("mpr");
      changeLayout("2x2");
    }
  };
  const toggleProjection = (mode: "mip" | "minip") => {
    if (volumeReason) return;
    setPlaying(false);
    // The design's own rule: pressing the mode that is on returns to MPR.
    setView((v) => (v === mode ? "mpr" : mode));
  };

  // A view that is no longer possible -- the series changed underneath
  // it, or the viewer broke -- falls back to the stack rather than
  // leaving a pressed button over a grid of series.
  useEffect(() => {
    if (view !== "stack" && volumeReason) setView("stack");
  }, [view, volumeReason]);

  // ------------------------------------------------------------------
  // Sync: the cells that can follow the active one BY POSITION do.
  //
  // NOT Cornerstone's slice synchroniser, and that was tried first. It
  // reads the position of every image in the target stack from the
  // image's own metadata -- which a wadouri image does not have until it
  // has been fetched -- so on a stack scrolled only part-way it throws,
  // and it throws inside `Promise.allSettled`, which swallows it. The
  // cell said "linked" and stayed where it was, with nothing in the
  // console. The positions stored at upload are known for every slice up
  // front, so the room uses those.
  // ------------------------------------------------------------------
  const geometries = cells.map((c, i): CellGeometry | null =>
    c === null || stacks[c]?.fallback !== null
      ? null
      : {
          frameOfReference: cellStates[i]?.geometry?.frameOfReference ?? null,
          normal: cellStates[i]?.geometry?.normal ?? null,
          series: c,
          positions: stacks[c]?.frames.map((f) => f.slice_position ?? null) ?? null,
        },
  );
  const links = linksTo(geometries, activeCell);
  const activeIndex = cellStates[activeCell]?.index ?? 0;
  const linkKey = links.join(",");
  useEffect(() => {
    if (!sync || volume || count < 2) return;
    const source = geometries[activeCell];
    if (!source) return;
    geometries.forEach((target, i) => {
      if (!target || links[i] !== "linked" || !cellStates[i]?.ready) return;
      const next = syncIndex(source, activeIndex, target);
      if (next !== null && next !== cellStates[i]?.index) handles.current[i]?.goTo(next);
    });
    // Driven by the active cell's index; everything else it reads is
    // current on that render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, activeCell, sync, volume, count, linkKey, readyCells]);

  // ------------------------------------------------------------------
  // Reference lines: where the ACTIVE cell's slice cuts every other cell,
  // in the active cell's plane colour.
  //
  // DRAWN HERE, NOT BY CORNERSTONE'S ReferenceLinesTool, which was tried
  // first. That tool finds the source viewport in `getRenderingEngines()
  // [0]` -- the first engine on the page, not the one it was given -- so
  // with any other viewer's engine alive it silently stops updating: the
  // coronal kept a line from 150 slices ago, the sagittal never got one,
  // and nothing was logged. The cut is plain geometry from the two
  // images' own headers, and the line is projected through the target's
  // own world-to-canvas mapping, which is what annotations use.
  // ------------------------------------------------------------------
  const sourceColour = planeColourFor(cellStates[activeCell]?.geometry?.normal ?? null);
  const [cuts, setCuts] = useState<(CellLine | null)[]>([]);
  useEffect(() => {
    if (!refLines || volume || count < 2) {
      setCuts((prev) => (prev.length ? [] : prev));
      return;
    }
    const source = handles.current[activeCell]?.plane();
    const sourceFrame = cellStates[activeCell]?.geometry?.frameOfReference;
    const corners = source ? imageCorners(source) : null;
    setCuts(
      cells.map((c, i) => {
        if (i === activeCell || c === null || !corners || !cellStates[i]?.ready) return null;
        // Two coordinate systems that only look alike would draw a line
        // through the wrong anatomy.
        if (!sourceFrame || cellStates[i]?.geometry?.frameOfReference !== sourceFrame) return null;
        const handle = handles.current[i];
        const target = handle?.plane();
        if (!handle || !target) return null;
        const cut = planeCut(corners, target.position, planeNormal(target));
        const a = cut && handle.toCanvas(cut[0]);
        const b = cut && handle.toCanvas(cut[1]);
        return a && b ? { x1: a[0], y1: a[1], x2: b[0], y2: b[1], colour: sourceColour } : null;
      }),
    );
    // Recomputed whenever any cell reports -- a scroll, a zoom, a pan --
    // which is every change that can move a line.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refLines, volume, count, activeCell, cellStates, cells, sourceColour]);

  // Cine. A clip loops itself at its OWN frame time, read from the file,
  // and keeps to the clock (`cineTarget`): the frame shown is the one due
  // by now, so slow decoding drops frames rather than slowing the clip.
  // Stills and slices step through the series one per tick.
  const indexRef = useRef(0);
  indexRef.current = vs.index;
  const entryHere = !volume ? stack?.entries[vs.index] : undefined;
  const cineMs = cineInterval(entryHere, vs.frameTime, ultrasound);
  const instanceHere = entryHere?.instance ?? -1;
  useEffect(() => {
    if (!playing || total < 2) return;
    let anchor: { time: number; index: number } | null = null;
    const id = window.setInterval(() => {
      const i = indexRef.current;
      const here = !volume ? stack?.entries[i] : undefined;
      if (here?.frame != null && stack) {
        const now = performance.now();
        anchor ??= { time: now, index: i };
        const target = cineTarget(stack.entries, anchor.index, Math.floor((now - anchor.time) / cineMs));
        if (target !== i) current()?.goTo(target);
        return;
      }
      const next = !volume && stack ? nextCineIndex(stack.entries, i) : i + 1 >= total ? 0 : i + 1;
      current()?.goTo(next);
    }, cineMs);
    return () => window.clearInterval(id);
  }, [playing, total, current, cineMs, volume, stack, instanceHere]);

  // The tags of the image on screen, read from the file the viewport has
  // already parsed -- no second fetch, and exactly the bytes being read.
  const shownImageId = !volume ? imageIds[vs.index] : undefined;
  useEffect(() => {
    if (tab !== "tags" || !live || !vs.ready || !shownImageId) return;
    let cancelled = false;
    void init()
      .then((cs) => {
        const { url } = cs.loader.wadouri.parseImageId(shownImageId);
        const ds = cs.loader.wadouri.dataSetCacheManager.get(url) as unknown as TagSource | undefined;
        if (!cancelled) setTags(ds ? describeTags(ds) : null);
      })
      .catch(() => {
        if (!cancelled) setTags(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, live, vs.ready, shownImageId]);

  // From the panel back to the image a measurement is on: the cell
  // already showing its series if there is one, else the active cell.
  const pendingJump = useRef<{ cell: number; index: number } | null>(null);
  const jumpTo = useCallback(
    (m: Measurement) => {
      const where = placement.get(m.imageId);
      if (!where) return;
      setView("stack");
      const showing = cells.findIndex((c) => c === where.series);
      if (showing >= 0) {
        setActiveCell(showing);
        if (!volume && cellStates[showing]?.ready) handles.current[showing]?.goTo(where.index);
        else pendingJump.current = { cell: showing, index: where.index };
      } else {
        loadSeries(where.series);
        pendingJump.current = { cell: activeCell, index: where.index };
      }
    },
    [placement, cells, volume, cellStates, loadSeries, activeCell],
  );
  const readiness = cellStates.map((s) => (s.ready ? 1 : 0)).join("");
  useEffect(() => {
    const p = pendingJump.current;
    if (p && cellStates[p.cell]?.ready && handles.current[p.cell]) {
      handles.current[p.cell]?.goTo(p.index);
      pendingJump.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readiness]);

  const removeMeasurement = useCallback((uid: string) => {
    void init()
      .then((cs) => {
        cs.tools.annotation.state.removeAnnotation(uid);
        // `render()` repaints the IMAGE; the annotation layer is a
        // separate SVG, and without this the ruler stayed drawn after the
        // panel had dropped it -- on every cell showing that image.
        cs.tools.utilities.triggerAnnotationRenderForToolGroupIds([TOOL_GROUP_ID]);
        // On a volume view no cell is listening, so the panel is told here.
        setMeasurements((prev) => prev.filter((m) => m.uid !== uid));
      })
      .catch(() => undefined);
  }, []);

  // ------------------------------------------------------------------
  // Embedded: armed by a pointer inside the room, disarmed by one outside.
  // ------------------------------------------------------------------
  const rootRef = useRef<HTMLDivElement | null>(null);
  const armedRef = useRef(mode === "full");
  useEffect(() => {
    armedRef.current = mode === "full";
    if (mode === "full") return;
    const onDown = (event: PointerEvent) => {
      armedRef.current = !!rootRef.current?.contains(event.target as Node);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [mode]);

  // ------------------------------------------------------------------
  // The series, reported to a page that lists them itself -- the
  // reading screen's own Series pane, with its opened-ticks.
  // ------------------------------------------------------------------
  const [seen, setSeen] = useState<Set<number>>(() => new Set([0]));
  useEffect(() => {
    setSeen((prev) => {
      const next = new Set(prev);
      for (const c of cells) if (c !== null) next.add(c);
      return next.size === prev.size ? prev : next;
    });
  }, [cells]);
  const loadRef = useRef(loadSeries);
  loadRef.current = loadSeries;
  useEffect(() => {
    if (!onSeries || !series.length) return;
    onSeries({
      series: series.map((s, i) => ({
        key: s.key,
        label: s.label,
        count: stacks[i]?.instances || s.images.length,
        seen: seen.has(i),
      })),
      index: activeSeries ?? 0,
      unseen: series.filter((_, i) => !seen.has(i)).length,
      select: (i) => loadRef.current(i),
    });
  }, [onSeries, series, stacks, seen, activeSeries]);

  // ------------------------------------------------------------------
  // The keyboard. The room is full-screen and modal in effect, so it
  // owns the keys -- except while somebody is typing.
  // ------------------------------------------------------------------
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // EMBEDDED, the room shares the page with a recorder and a form, so
      // it takes keys only once the reader has put the pointer in it.
      if (!armedRef.current) return;
      // The label sheet owns the keyboard while it is open -- and so does
      // any of the app's own dialogs raised over the room (a sitting's
      // confirmations, the idle warning), or Escape meant for the dialog
      // would leave full screen instead and never reach it.
      if (askingRef.current || document.querySelector("[role=alertdialog]")) return;
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) && event.key !== "Escape") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const preset = ultrasound ? undefined : presets.find((p) => p.key === event.key);
      if (preset) {
        current()?.applyWindow(preset.width, preset.centre);
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const action = actionForKey(event.key, event.shiftKey);
      if (!action) return;
      if (ultrasound && !usAllows(action)) return;
      const vp = current();
      switch (action.kind) {
        case "escape":
          if (view !== "stack") setView("stack");
          else if (mode === "full") onClose();
          // Embedded on the stack, Escape belongs to the page.
          else return;
          break;
        case "series":
          loadSeries((activeSeries ?? 0) + action.delta);
          break;
        case "overlays":
          setOverlays((v) => !v);
          break;
        case "tags":
          setTab((t) => (t === "tags" ? "meas" : "tags"));
          break;
        case "layout":
          changeLayout(action.layout);
          break;
        case "refLines":
          setRefLines((v) => !v);
          break;
        case "sync":
          if (!volume) setSync((v) => !v);
          break;
        case "mpr":
          toggleMpr();
          break;
        case "projection":
          toggleProjection(action.mode);
          break;
        case "volumeRender":
          break;
        case "cine":
          if (live) setPlaying((v) => !v);
          break;
        default:
          if (!live) return;
          if (action.kind === "tool") {
            // On a volume view only the navigate tools apply, and only
            // while the crosshair is not holding the left button.
            const navigate = NAVIGATE.some((t) => t.id === action.tool);
            if (!volume || (navigate && !crosshair)) setTool(action.tool);
          } else if (action.kind === "rotate" && !volume) vp?.rotate(action.degrees);
          else if (action.kind === "flip" && !volume) vp?.flip(action.axis);
          else if (action.kind === "invert") vp?.toggleInvert();
          else if (action.kind === "reset") vp?.reset();
          else if (action.kind === "image") vp?.step(action.delta);
          else if (action.kind === "first") vp?.goTo(0);
          else if (action.kind === "last") vp?.goTo(total - 1);
      }
      event.preventDefault();
      // THE ROOM IS MODAL, so a key it handled goes no further. It opens
      // over screens that listen for Escape themselves -- the screening
      // console, the past-round reviewer -- and one Escape closed the
      // room AND the console under it. Listening in the CAPTURE phase is
      // what lets this run first and stop it.
      event.stopPropagation();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  });

  // ------------------------------------------------------------------
  // Toolbar pieces.
  // ------------------------------------------------------------------
  const notLive = !live ? "this series holds only rendered images" : null;
  const onReformat = volume ? "only on the stack, not on a reformat" : null;
  const navReason = notLive ?? (crosshair ? "the left button moves the crosshair; turn reference lines off to choose another" : null);
  const measureReason = notLive ?? (volume ? "measure on the stack; leave the reformat first" : null);

  const toolButton = (t: { id: RoomTool; label: string; key: string }, icon: string, reason: string | null) => (
    <button
      key={t.id}
      type="button"
      className="rr-tool"
      data-cy={`rr-tool-${t.id}`}
      aria-label={t.label}
      aria-pressed={tool === t.id}
      title={reason ? `${t.label}: ${reason}` : `${t.label}  (${t.key})`}
      disabled={!!reason}
      onClick={() => setTool(t.id)}
    >
      <Icon d={icon} />
    </button>
  );
  const actionButton = (
    cy: string,
    label: string,
    key: string,
    icon: string,
    onClick: () => void,
    pressed?: boolean,
    disabledReason?: string | null,
  ) => (
    <button
      type="button"
      className="rr-tool"
      data-cy={cy}
      aria-label={label}
      aria-pressed={pressed}
      title={disabledReason ? `${label}: ${disabledReason}` : `${label}  (${key})`}
      disabled={!!disabledReason}
      onClick={onClick}
    >
      <Icon d={icon} />
    </button>
  );
  const wordButton = (
    cy: string,
    label: string,
    key: string,
    word: string,
    onClick: () => void,
    pressed: boolean,
    disabledReason: string | null,
    box = false,
  ) => (
    <button
      key={cy}
      type="button"
      className="rr-tool"
      data-cy={cy}
      aria-label={label}
      aria-pressed={pressed}
      title={disabledReason ? `${label}: ${disabledReason}` : `${label}  (${key})`}
      disabled={!!disabledReason}
      onClick={onClick}
    >
      <span className={box ? "rr-box" : "rr-word"}>{word}</span>
    </button>
  );
  const navIcons: Record<string, string> = { pan: ICON.pan, zoom: ICON.zoom, wl: ICON.wl, stack: ICON.stack };
  const measureIcons: Record<string, string> = {
    length: ICON.length,
    angle: ICON.angle,
    cobb: ICON.cobb,
    probe: ICON.probe,
    ellipse: ICON.ellipse,
    rect: ICON.rect,
    ratio: ICON.ratio,
    text: ICON.text,
    erase: ICON.erase,
  };

  const status = (testCase.status ?? "").toUpperCase();
  const statusPill =
    status === "READY" ? "Published" : status === "ARCHIVED" ? "Archived" : "Draft — not yet in an exam";
  const sexAge = [testCase.patient_sex, testCase.patient_age].filter(Boolean).join(" · ");
  const titleLines = [sexAge, [modality, testCase.body_part].filter(Boolean).join(" · ")].filter(Boolean);
  const activeTool = toolById(tool);
  const usTools = new Set<RoomTool>(US_TOOLS);
  function usAllows(action: NonNullable<ReturnType<typeof actionForKey>>): boolean {
    if (action.kind === "tool") return usTools.has(action.tool);
    return !["mpr", "projection", "volumeRender", "refLines"].includes(action.kind);
  }
  const hint = crosshair
    ? { label: "Crosshair", text: "Drag to move the crosshair; the other planes follow" }
    : { label: activeTool.label, text: activeTool.hint };

  // What the rail says a series holds: its slices or stills, and its
  // clips by their real frame count -- not the stored JPEG rows, which
  // for a clip are a subsample (a 90-frame clip keeps 60).
  const railCount = (i: number) => {
    const st = stacks[i];
    const rows = series[i]?.images.length ?? 0;
    if (!st || st.fallback !== null) return `${rows} img`;
    const clips = new Map<number, number>();
    for (const e of st.entries) if (e.frame !== null) clips.set(e.instance, e.frames);
    const stills = st.instances - clips.size;
    const parts = [
      stills ? `${stills} img` : null,
      clips.size === 1 ? `1 clip · ${[...clips.values()][0]} fr` : clips.size ? `${clips.size} clips` : null,
    ].filter(Boolean);
    return parts.join(" · ");
  };

  const emptyCell = (i: number, text: string) => (
    <div
      key={`empty-${i}`}
      className="rr-cell rr-cell--empty"
      data-cy="rr-cell-empty"
      data-active={i === activeCell || undefined}
      style={{ borderColor: count > 1 && i === activeCell ? "#5cc8b8" : "#1f252d" }}
      onMouseDown={() => setActiveCell(i)}
    >
      <p className="rr-empty-note">{text}</p>
    </div>
  );

  // ONE HOST ELEMENT FOR THE WHOLE LIFE OF THE ROOM, moved between the
  // page's pane and document.body as the mode changes. Rendering the room
  // in two places would remount it -- new engines, the stack back to its
  // first image, every measurement gone -- for the act of pressing "Full
  // screen". Moving the host moves the canvases with it, and each cell's
  // ResizeObserver refits them. In the body it also escapes any
  // transformed ancestor, which `position: fixed` resolves against.
  const [host] = useState(() => document.createElement("div"));
  const anchorRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    host.className = mode === "full" ? "" : "rr-host";
    const target = mode === "full" ? document.body : anchorRef.current;
    if (target && host.parentElement !== target) target.appendChild(host);
  });
  useLayoutEffect(() => () => host.remove(), [host]);

  // FOLDING, full screen only. Each side column -- the host's dock, the
  // series, the panel -- folds to a strip so the images can take the
  // width. The DOCK IS HIDDEN, NEVER UNMOUNTED: a recorder or a camera
  // lives in it, and taking it out of the page would end the take.
  const [ownFolds, setOwnFolds] = useState<RoomFolds>(folds ?? NO_FOLDS);
  const fold = mode === "full" ? (folds ?? ownFolds) : NO_FOLDS;
  const setFold = (key: keyof RoomFolds, value: boolean) => {
    const next = { ...fold, [key]: value };
    setOwnFolds(next);
    onFoldsChange?.(next);
  };
  const strip = "44px";

  const showRail = mode === "full" || showSeriesRail;
  const dockColumn = fold.dock ? strip : "clamp(300px, 24vw, 440px)";
  const workColumns = [
    dockSide === "left" && mode === "full" ? dockColumn : null,
    showRail ? (fold.rail ? strip : "clamp(200px, 16vw, 280px)") : null,
    "minmax(0, 1fr)",
    mode === "full" ? (fold.panel ? strip : "clamp(320px, 26vw, 440px)") : null,
    dockSide === "right" && mode === "full" ? dockColumn : null,
  ]
    .filter(Boolean)
    .join(" ");

  // The heading is the dock's FIRST child and the same element folded or
  // not: the host's controls are appended after it by `Movable`, outside
  // React, and a heading that was swapped for another element would be
  // re-inserted after them.
  const dockColumnEl = (side: "left" | "right") => (
    <aside
      aria-label={dockLabel}
      className={fold.dock ? "rr-dock rr-dock--folded" : "rr-dock"}
      data-cy="rr-dock"
      data-folded={fold.dock ? "true" : "false"}
      ref={onDock}
    >
      <div className={fold.dock ? "rr-dockhead rr-strip" : "rr-dockhead"}>
        {fold.dock ? (
          <FoldStrip id="dock" label={dockLabel} side={side} onOpen={() => setFold("dock", false)}>
            {dockStrip}
          </FoldStrip>
        ) : (
          <>
            {side === "right" && <FoldButton id="dock" label={dockLabel} side={side} onFold={() => setFold("dock", true)} />}
            <h2 className="rr-h2">{dockLabel}</h2>
            {side === "left" && <FoldButton id="dock" label={dockLabel} side={side} onFold={() => setFold("dock", true)} />}
          </>
        )}
      </div>
    </aside>
  );

  const room = (
    <div
      ref={rootRef}
      className={mode === "embedded" ? "rr-root rr-root--embedded" : "rr-root"}
      data-cy={mode === "embedded" ? "reading-room-embedded" : "reading-room"}
      role={mode === "embedded" ? "region" : "dialog"}
      aria-label={`Reading room — ${testCase.title}`}
    >
      {/* -- toolbar --------------------------------------------------- */}
      <div role="toolbar" aria-label="Image tools" className="rr-toolbar">
{ultrasound ? (
          <>
        <div role="group" aria-label="Navigate" className="rr-group">
          {NAVIGATE.filter((t) => usTools.has(t.id)).map((t) => toolButton(t, navIcons[t.id]!, navReason))}
        </div>
        <div role="group" aria-label="Orient" className="rr-group">
          {actionButton("rr-rotate-left", "Rotate left", "[", ICON.rotL, () => current()?.rotate(-90), undefined, notLive)}
          {actionButton("rr-rotate-right", "Rotate right", "]", ICON.rotR, () => current()?.rotate(90), undefined, notLive)}
          {actionButton("rr-flip-v", "Flip vertical", "V", ICON.flipV, () => current()?.flip("v"), undefined, notLive)}
          {actionButton("rr-flip-h", "Flip horizontal", "H", ICON.flipH, () => current()?.flip("h"), undefined, notLive)}
          {actionButton("rr-invert", "Invert greyscale", "I", ICON.invert, () => current()?.toggleInvert(), vs.inverted, notLive)}
          {actionButton("rr-reset", "Reset view", "0", ICON.reset, () => current()?.reset(), undefined, notLive)}
        </div>
        <div role="group" aria-label="Measure" className="rr-group">
          {US_MEASURE.map((id) => toolById(id)).map((t) => toolButton(t, measureIcons[t.id]!, measureReason))}
        </div>
        <div role="group" aria-label="Display" className="rr-group">
          {actionButton("rr-tile", "Tile images in series", "G", ICON.tile, () => undefined, false, "not built yet")}
          {actionButton("rr-overlays", overlays ? "Hide overlays" : "Show overlays", "O", ICON.eye, () => setOverlays((v) => !v), overlays)}
          {actionButton("rr-tags", "DICOM tags", "D", ICON.tags, () => setTab((t) => (t === "tags" ? "meas" : "tags")), tab === "tags")}
        </div>
        <div role="group" aria-label="Series" className="rr-group">
          {actionButton("rr-prev-series", "Previous series", "PgUp", ICON.prevS, () => loadSeries((activeSeries ?? 0) - 1), undefined, (activeSeries ?? 0) === 0 ? "this is the first series" : null)}
          {actionButton("rr-next-series", "Next series", "PgDn", ICON.nextS, () => loadSeries((activeSeries ?? 0) + 1), undefined, (activeSeries ?? 0) >= series.length - 1 ? "this is the last series" : null)}
        </div>
        <div role="group" aria-label="Cine" className="rr-group">
          {actionButton("rr-cine-tool", playing ? "Pause cine" : "Play cine", "Space", playing ? ICON.pause : ICON.film, () => setPlaying((v) => !v), playing, total < 2 ? "one image, nothing to play" : notLive)}
          {actionButton("rr-sync-tool", "Sync scrolling", "Y", ICON.sync, () => setSync((v) => !v), sync)}
        </div>
        <div role="group" aria-label="Layout" className="rr-group">
          {wordButton("rr-layout-1x1", "1 × 1 layout", "1", "1", () => changeLayout("1x1"), layout === "1x1", null, true)}
          {wordButton("rr-layout-2x1", "2 × 1 layout", "2", "2x1", () => changeLayout("2x1"), layout === "2x1", null, true)}
          {wordButton("rr-layout-2x2", "2 × 2 layout", "4", "2x2", () => changeLayout("2x2"), layout === "2x2", null, true)}
        </div>
          </>
        ) : (
          <>
        <div role="group" aria-label="Navigate" className="rr-group">
          {NAVIGATE.map((t) => toolButton(t, navIcons[t.id]!, navReason))}
        </div>
        {presets.length > 0 && (
          <div role="group" aria-label="Window presets" className="rr-group">
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                className="rr-tool"
                data-cy={`rr-preset-${p.short.toLowerCase()}`}
                aria-label={`${p.label} window`}
                aria-pressed={matchedPreset?.key === p.key}
                title={`${p.label} window — W ${p.width} L ${p.centre}  (${p.key})`}
                disabled={!!notLive}
                onClick={() => current()?.applyWindow(p.width, p.centre)}
              >
                <span className="rr-word">{p.short}</span>
              </button>
            ))}
          </div>
        )}
        <div role="group" aria-label="Orient" className="rr-group">
          {actionButton("rr-rotate-left", "Rotate left", "[", ICON.rotL, () => current()?.rotate(-90), undefined, notLive ?? onReformat)}
          {actionButton("rr-rotate-right", "Rotate right", "]", ICON.rotR, () => current()?.rotate(90), undefined, notLive ?? onReformat)}
          {actionButton("rr-flip-h", "Flip horizontal", "H", ICON.flipH, () => current()?.flip("h"), undefined, notLive ?? onReformat)}
          {actionButton("rr-flip-v", "Flip vertical", "V", ICON.flipV, () => current()?.flip("v"), undefined, notLive ?? onReformat)}
          {actionButton("rr-invert", "Invert greyscale", "I", ICON.invert, () => current()?.toggleInvert(), vs.inverted, notLive)}
          {actionButton("rr-reset", "Reset view", "0", ICON.reset, () => current()?.reset(), undefined, notLive)}
        </div>
        <div role="group" aria-label="Measure" className="rr-group">
          {MEASURE.map((t) => toolButton(t, measureIcons[t.id]!, measureReason))}
        </div>
        <div role="group" aria-label="MPR and 3D" className="rr-group">
          {/* DISABLED WITH THE REASON, never hidden -- the MPR rule. */}
          {actionButton("rr-mpr", "MPR — axial, coronal, sagittal", "M", ICON.mpr, toggleMpr, view === "mpr", volumeReason)}
          {wordButton("rr-mip", "Maximum intensity projection", "Shift+M", "MIP", () => toggleProjection("mip"), view === "mip", volumeReason)}
          {wordButton("rr-minip", "Minimum intensity projection", "Shift+N", "MinIP", () => toggleProjection("minip"), view === "minip", volumeReason)}
          {wordButton("rr-3d", "3D volume rendering", "Shift+V", "3D", () => undefined, false, "not built yet; deferred in the scope, section 2.3")}
          {actionButton("rr-reflines", "Reference lines", "Shift+R", ICON.refLines, () => setRefLines((v) => !v), refLines)}
          {actionButton("rr-overlays", overlays ? "Hide overlays" : "Show overlays", "O", ICON.eye, () => setOverlays((v) => !v), overlays)}
        </div>
        <div role="group" aria-label="Study" className="rr-group">
          {actionButton("rr-tags", "DICOM tags", "D", ICON.tags, () => setTab((t) => (t === "tags" ? "meas" : "tags")), tab === "tags")}
        </div>
        <div role="group" aria-label="Layout" className="rr-group">
          {wordButton("rr-layout-1x1", "1 × 1 layout", "1", "1", () => changeLayout("1x1"), layout === "1x1", null, true)}
          {wordButton("rr-layout-2x1", volume ? "2 × 1 layout — axial and coronal" : "2 × 1 layout — two series", "2", "2x1", () => changeLayout("2x1"), layout === "2x1", null, true)}
          {wordButton("rr-layout-2x2", volume ? "2 × 2 layout — MPR" : "2 × 2 layout — four series", "4", "2x2", () => changeLayout("2x2"), layout === "2x2", null, true)}
        </div>
        <div role="group" aria-label="Series" className="rr-group">
          {actionButton("rr-prev-series", "Previous series", "PgUp", ICON.prevS, () => loadSeries((activeSeries ?? 0) - 1), undefined, (activeSeries ?? 0) === 0 ? "this is the first series" : null)}
          {actionButton("rr-next-series", "Next series", "PgDn", ICON.nextS, () => loadSeries((activeSeries ?? 0) + 1), undefined, (activeSeries ?? 0) >= series.length - 1 ? "this is the last series" : null)}
        </div>
          </>
        )}
        {mode === "full" ? (
          <button type="button" className="rr-close" data-cy="rr-close" onClick={onClose} title={`${closeLabel} (Esc)`}>
            <Icon d={ICON.close} size={16} />
            {closeLabel}
          </button>
        ) : (
          onFullScreen && (
            <button type="button" className="rr-close" data-cy="rr-fullscreen" onClick={onFullScreen} title="Full screen">
              <Icon d={ICON.expand} size={16} />
              Full screen
            </button>
          )
        )}
      </div>

      {/* -- case banner ----------------------------------------------- */}
      {mode === "full" && (
      <section aria-label="Case" className="rr-banner">
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 180 }}>
          <div className="rr-banner-title" data-cy="rr-case-title">{testCase.title}</div>
          <div className="rr-banner-sub">
            {/* "Not recorded" only when the host KNOWS -- a host that simply
                does not carry sex and age must not claim them absent. */}
            {sexAge || (testCase.patient_sex === undefined && testCase.patient_age === undefined ? "" : "Sex and age not recorded")}
          </div>
        </div>
        <div className="rr-fields">
          <div className="rr-field">
            <span className="rr-field-label">Modality</span>
            <span className="rr-mono">{testCase.modality || "—"}</span>
          </div>
          <div className="rr-field">
            <span className="rr-field-label">Body part</span>
            <span>{testCase.body_part || "—"}</span>
          </div>
          <div className="rr-field">
            <span className="rr-field-label">Images</span>
            <span className="rr-mono">
              {instances.length} · {series.length} series
            </span>
          </div>
          {testCase.clinical_history && (
            <div className="rr-field" style={{ maxWidth: 420 }}>
              <span className="rr-field-label">Clinical history</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {testCase.clinical_history}
              </span>
            </div>
          )}
        </div>
        {caseNav && (
          <div className="rr-casenav" role="group" aria-label="Cases in this sitting">
            <button
              type="button"
              className="rr-chip-btn"
              data-cy="rr-prev-case"
              disabled={caseNav.position <= 1}
              onClick={caseNav.onPrev}
            >
              <Icon d={ICON.prevI} size={16} />
              Previous case
            </button>
            <span className="rr-mono" data-cy="rr-case-position">
              Case {caseNav.position} of {caseNav.count}
            </span>
            <button
              type="button"
              className="rr-chip-btn"
              data-cy="rr-next-case"
              disabled={caseNav.position >= caseNav.count}
              onClick={caseNav.onNext}
            >
              Next case
              <Icon d={ICON.nextI} size={16} />
            </button>
          </div>
        )}
        {testCase.status && (
          <div className={status === "READY" ? "rr-pill rr-pill--ok" : "rr-pill"}>{statusPill}</div>
        )}
      </section>
      )}

      {mode === "full" && dockSide && fold.dock && (
        <section
          aria-label={`${dockLabel}, folded`}
          className="rr-dockbar"
          data-cy="rr-dockbar"
          ref={onDockBar}
        >
          {dockBar}
        </section>
      )}

      <div className="rr-work" style={{ "--rr-cols": workColumns } as CSSProperties}>
        {dockSide === "left" && mode === "full" && dockColumnEl("left")}
        {/* -- series rail ---------------------------------------------- */}
        {showRail && fold.rail && (
          <aside aria-label="Series" className="rr-rail rr-rail--folded rr-strip" data-cy="rr-rail-folded">
            <FoldStrip id="rail" label="Series" side="left" onOpen={() => setFold("rail", false)}>
              <span className="rr-strip-count">{series.length}</span>
            </FoldStrip>
          </aside>
        )}
        {showRail && !fold.rail && (
        <aside aria-label="Series" className="rr-rail">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h2 className="rr-h2">Series</h2>
            <span className="rr-muted" style={{ marginLeft: "auto" }}>
              {series.length} · {instances.length} images
            </span>
            {mode === "full" && <FoldButton id="rail" label="Series" side="left" onFold={() => setFold("rail", true)} />}
          </div>
          <div className="rr-series">
            {series.map((s, i) => {
              const mid = s.images[Math.floor(s.images.length / 2)];
              const number = s.images.find((x) => x.series_number != null)?.series_number;
              const shownIn = cells.flatMap((c, j) => (c === i ? [j + 1] : []));
              return (
                <button
                  key={s.key}
                  type="button"
                  className="rr-series-item"
                  data-cy="rr-series"
                  aria-pressed={i === activeSeries}
                  title={count > 1 ? "Show this series in the selected viewport" : undefined}
                  onClick={() => loadSeries(i)}
                >
                  {mid ? <img className="rr-thumb" src={mid.url} alt="" loading="lazy" /> : <span className="rr-thumb" />}
                  <span className="rr-series-text">
                    <span className="rr-series-title">{s.label}</span>
                    <span className="rr-muted rr-mono">
                      S{number ?? i + 1} · {railCount(i)}
                      {count > 1 && shownIn.length > 0 && !volume ? ` · in ${shownIn.join(", ")}` : ""}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </aside>
        )}

        {/* -- viewer --------------------------------------------------- */}
        <main className="rr-main">
          <div className="rr-stage">
            {query.isLoading ? (
              <div className="rr-cell">
                <p className="rr-loading">Loading images…</p>
              </div>
            ) : stageFallback ? (
              <>
                {explainFallback && stack?.fallback !== "empty" && (
                  <p data-cy="rr-fallback-note" className="rr-note" style={{ color: "#f2cb6b", margin: 0 }}>
                    {broken
                      ? `The diagnostic viewer could not start, so these are the rendered images. ${broken}`
                      : "Stored before the diagnostic viewer — showing the rendered images. Re-upload this case to read it live."}
                  </p>
                )}
                <div className="rr-cell" style={{ background: "#fff" }}>
                  <TestCaseViewer source={source} showSeriesRail={false} />
                </div>
              </>
            ) : volume && active ? (
              <RoomVolume
                key={`${active.key}:${layout}`}
                ref={volumeHandle}
                volumeKey={`${nonce}-${activeSeries}`}
                imageIds={imageIds}
                modality={modality}
                layout={layout}
                view={view}
                slab={slab}
                refLines={refLines}
                navTool={tool}
                overlays={overlays}
                title={testCase.title}
                titleLines={titleLines}
                windowName={(voi) => presetFor(voi)?.label ?? null}
                getEngine={getVolumeEngine}
                onState={setPlane}
                onUnavailable={setBroken}
              />
            ) : (
              <div className={`rr-grid rr-grid--${layout}`} data-cy="rr-grid">
                {cells.map((si, i) => {
                  if (si === null) return emptyCell(i, "Pick a series from the list to show it here.");
                  const st = stacks[si];
                  const s = series[si];
                  if (!st || !s) return emptyCell(i, "Pick a series from the list to show it here.");
                  if (st.fallback !== null) {
                    return emptyCell(i, `${s.label}: rendered images only — this series cannot be read live.`);
                  }
                  const c = cellStates[i] ?? EMPTY_VIEWPORT;
                  const isActive = i === activeCell;
                  const entry = st.entries[c.index];
                  const us = isUltrasound(c.modality ?? testCase.modality);
                  const position = st.frames[c.index]?.slice_position;
                  const name = presetFor(c.voi);
                  const unit = c.modality?.toUpperCase() === "CT" ? "HU" : "";
                  return (
                    <RoomCell
                      key={`${i}:${s.key}`}
                      cy="rr-cell"
                      label={s.label}
                      colour={planeColourFor(c.geometry?.normal ?? null)}
                      active={isActive}
                      framed={count > 1}
                      overlays={overlays && c.ready}
                      letters={c.letters}
                      note={sync && count > 1 ? LINK_NOTE[links[i] ?? "empty"] : null}
                      line={cuts[i] ?? null}
                      onActivate={() => setActiveCell(i)}
                      corners={{
                        tl: (
                          <>
                            <span className="rr-bright">{testCase.title}</span>
                            {titleLines.map((t) => (
                              <span key={t}>{t}</span>
                            ))}
                          </>
                        ),
                        tr: us ? (
                          entry?.frame != null ? (
                            <>
                              <span className="rr-bright" data-cy={isActive ? "rr-clip-rate" : undefined}>
                                Clip{c.frameTime ? ` · ${Math.round(1000 / c.frameTime)} Hz` : ""}
                              </span>
                              <span data-cy={isActive ? "rr-frame" : undefined}>
                                Frame {entry.frame}/{entry.frames}
                              </span>
                            </>
                          ) : (
                            <span className="rr-bright">Still</span>
                          )
                        ) : (
                          <>
                            <span className="rr-bright" data-cy={isActive ? "rr-position" : "rr-cell-position"}>
                              Im {c.index + 1}/{st.imageIds.length}
                            </span>
                            {c.thickness != null && <span>Thk {c.thickness.toFixed(1)} mm</span>}
                            {c.value != null && (
                              <span data-cy={isActive ? "rr-value" : undefined}>
                                {c.value} {unit}
                              </span>
                            )}
                          </>
                        ),
                        bl: us ? (
                          <span>
                            Zoom {c.zoom.toFixed(1)}×{c.voi ? ` · W ${c.voi.width} L ${c.voi.centre}` : ""}
                          </span>
                        ) : (
                          <>
                            <span>Zoom {c.zoom.toFixed(1)}×</span>
                            {c.voi && (
                              <span className="rr-bright" data-cy={isActive ? "rr-window" : undefined}>
                                W {c.voi.width} L {c.voi.centre}
                                {name ? ` · ${name.label}` : ""}
                              </span>
                            )}
                          </>
                        ),
                        br: us ? (
                          <span className="rr-bright" data-cy={isActive ? "rr-position" : "rr-cell-position"}>
                            Img {(entry?.instance ?? 0) + 1}/{st.instances}
                          </span>
                        ) : typeof position === "number" ? (
                            <span className="rr-bright">Loc {position.toFixed(1)} mm</span>
                          ) : undefined,
                      }}
                    >
                      <RoomViewport
                        ref={(h) => {
                          handles.current[i] = h;
                        }}
                        imageIds={st.imageIds}
                        caseImageIds={caseImageIds}
                        tool={tool}
                        getEngine={getEngine}
                        onState={cellSetters[i]!}
                        onMeasurements={setMeasurements}
                        onUnavailable={setBroken}
                      />
                      {!c.ready && <p className="rr-loading">Loading the viewer…</p>}
                    </RoomCell>
                  );
                })}
              </div>
            )}

            {!query.isLoading && !stageFallback && (
              <div role="group" aria-label="Image navigation" className="rr-nav">
                <div style={{ display: "flex", gap: 2 }}>
                  <button type="button" className="rr-nav-btn" aria-label="Previous image" title="Previous image (Up arrow)" disabled={total < 2} onClick={() => current()?.step(-1)}>
                    <Icon d={ICON.prevI} />
                  </button>
                  <button
                    type="button"
                    className="rr-chip-btn"
                    data-cy="rr-cine"
                    aria-pressed={playing}
                    title="Cine (Space)"
                    disabled={total < 2}
                    onClick={() => setPlaying((v) => !v)}
                  >
                    <Icon d={playing ? ICON.pause : ICON.play} size={16} fill />
                    {playing ? "Pause" : "Play"}
                  </button>
                  <button type="button" className="rr-nav-btn" aria-label="Next image" title="Next image (Down arrow)" disabled={total < 2} onClick={() => current()?.step(1)}>
                    <Icon d={ICON.nextI} />
                  </button>
                </div>
                <label className="rr-slider">
                  {volume ? "Slice" : "Image"}
                  <input
                    type="range"
                    data-cy="rr-slider"
                    min={1}
                    max={Math.max(total, 1)}
                    value={Math.min(vs.index + 1, Math.max(total, 1))}
                    disabled={total < 2}
                    onChange={(e) => current()?.goTo(Number(e.target.value) - 1)}
                  />
                </label>
                <span className="rr-count" data-cy="rr-count">
                  {ultrasound && entryHere
                    ? `${entryHere.instance + 1} / ${stack?.instances ?? 0}${entryHere.frame != null ? ` · ${entryHere.frame}/${entryHere.frames}` : ""}`
                    : `${total ? vs.index + 1 : 0} / ${total}`}
                </span>
                {!ultrasound && (
                <label
                  className="rr-slab"
                  title={view === "mip" || view === "minip" ? "Slab thickness" : "Slab: applies to MIP and MinIP"}
                >
                  Slab
                  <select
                    className="rr-select"
                    data-cy="rr-slab"
                    value={slab}
                    disabled={view !== "mip" && view !== "minip"}
                    onChange={(e) => setSlab(Number(e.target.value) as Slab)}
                  >
                    {SLABS.map((mm) => (
                      <option key={mm} value={mm}>
                        {mm} mm
                      </option>
                    ))}
                  </select>
                </label>
                )}
                <button
                  type="button"
                  className="rr-chip-btn"
                  data-cy="rr-sync"
                  aria-pressed={sync}
                  disabled={volume}
                  title={
                    volume
                      ? "Sync: the planes of a reformat already follow the crosshair"
                      : "Link scrolling across viewports (Y)"
                  }
                  onClick={() => setSync((v) => !v)}
                >
                  <Icon d={ICON.sync} size={16} />
                  Sync
                </button>
              </div>
            )}
          </div>

          <footer className="rr-footer">
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="rr-tag" data-cy="rr-active-tool">{hint.label}</span>
              <span>{hint.text}</span>
            </span>
            {vs.voi && (
              <span style={{ color: "#c3cbd5" }}>
                W {vs.voi.width} L {vs.voi.centre}
                {matchedPreset ? ` · ${matchedPreset.label}` : ""}
              </span>
            )}
            {live && (
              <span className="rr-ok">
                <Icon d={ICON.check} size={14} />
                Lossless — diagnostic quality
              </span>
            )}
            <span className="rr-mono" style={{ marginLeft: "auto" }}>
              {measurements.length} measurement{measurements.length === 1 ? "" : "s"}
            </span>
          </footer>
        </main>

        {/* -- panel ---------------------------------------------------- */}
        {mode === "full" && (
        <aside
          aria-label="Measurements and case"
          className={fold.panel ? "rr-panel rr-panel--folded" : "rr-panel"}
          data-cy="rr-panel"
          data-folded={fold.panel ? "true" : "false"}
        >
          {fold.panel && (
            <div className="rr-strip">
              <FoldStrip id="panel" label="Measurements · Case · Tags" side="right" onOpen={() => setFold("panel", false)}>
                {measurements.length > 0 && <span className="rr-strip-count">{measurements.length}</span>}
              </FoldStrip>
            </div>
          )}
          <div role="tablist" aria-label="Panel" className="rr-tabs">
            <span style={{ display: "flex", alignItems: "center", marginRight: 4 }}>
              <FoldButton id="panel" label="the panel" side="right" onFold={() => setFold("panel", true)} />
            </span>
            {(
              [
                ...(extraTab ? ([["extra", extraTab.label]] as const) : []),
                ["meas", "Measurements"],
                ["case", "Case"],
                ["tags", "Tags"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                data-cy={`rr-tab-${id}`}
                aria-selected={tab === id}
                className="rr-tab"
                onClick={() => setTab(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === "extra" && extraTab ? (
            <div className="rr-panel-body" data-cy="rr-extra">
              {extraTab.content}
            </div>
          ) : tab === "meas" ? (
            <div className="rr-panel-body">
              <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                  <h3 className="rr-h2">Measurements</h3>
                  <span className="rr-muted">this session</span>
                </div>
                {measurements.length === 0 ? (
                  <p className="rr-muted" data-cy="rr-no-measurements" style={{ margin: 0 }}>
                    Draw with Length, Angle, Cobb, Ratio, the probe, an ROI or a text label and the readings
                    appear here.
                  </p>
                ) : (
                  <div className="rr-list" data-cy="rr-measurements">
                    {measurements.map((m) => {
                      const where = placement.get(m.imageId);
                      return (
                        <div key={m.uid} className="rr-row" data-cy="rr-measurement">
                          <button
                            type="button"
                            onClick={() => jumpTo(m)}
                            title="Go to this image"
                            style={{ all: "unset", cursor: "pointer", display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}
                          >
                            <span style={{ fontWeight: 500 }}>{m.label}</span>
                            <span className="rr-muted rr-mono">
                              {[
                                m.detail,
                                where ? `S${(series[where.series]?.images.find((x) => x.series_number != null)?.series_number) ?? where.series + 1} Im ${where.index + 1}` : null,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          </button>
                          <span className="rr-row-value">{m.value}</span>
                          <button
                            type="button"
                            className="rr-icon-btn"
                            aria-label={`Remove ${m.label}`}
                            title="Remove"
                            onClick={() => removeMeasurement(m.uid)}
                          >
                            <Icon d={ICON.close} size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
              <div className="rr-note">
                <span style={{ color: "#a9b4c2", flex: "none", marginTop: 2 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <path d={ICON.info} />
                  </svg>
                </span>
                <span style={{ fontSize: 12, color: "#a9b4c2" }}>
                  Measurements are for reading. Nothing here is saved, and they go when the reading room closes.
                </span>
              </div>
            </div>
          ) : tab === "tags" ? (
            <div className="rr-panel-body" data-cy="rr-tags">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                <h3 className="rr-h2">DICOM tags</h3>
                {live && !volume && (
                  <span className="rr-muted rr-mono">
                    Im {vs.index + 1}/{imageIds.length}
                  </span>
                )}
              </div>
              {!live ? (
                <p className="rr-muted" style={{ margin: 0 }}>
                  This series holds only rendered images, so there is no file to read tags from.
                </p>
              ) : volume ? (
                <p className="rr-muted" style={{ margin: 0 }}>
                  A reformat is built from many files, not one. Leave it to read the tags of the image on the
                  stack.
                </p>
              ) : !tags ? (
                <p className="rr-muted" style={{ margin: 0 }}>
                  Reading the file…
                </p>
              ) : (
                <>
                  <p className="rr-muted" style={{ margin: 0, fontSize: 12 }}>
                    Everything the stored file for this image holds. It is built from an allowlist at upload,
                    so there is nothing identifying here to show.
                  </p>
                  <div className="rr-list">
                    {tags.map((t) => (
                      <div key={t.tag} className="rr-tagrow" data-cy="rr-tag">
                        <span className="rr-tagrow-head">
                          <span className="rr-mono rr-muted">{t.tag}</span>
                          <span className="rr-mono rr-muted">{t.vr}</span>
                        </span>
                        <span className="rr-tagrow-name" title={t.keyword ?? undefined}>
                          {t.name}
                        </span>
                        <span className="rr-mono rr-tagrow-value">{t.value || "—"}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="rr-panel-body">
              <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <h3 className="rr-h2">Clinical history</h3>
                <p data-cy="rr-history" style={{ margin: 0, color: testCase.clinical_history ? "#e4e8ee" : "#8d97a5" }}>
                  {testCase.clinical_history || "This case has no clinical history."}
                </p>
              </section>
              <div className="rr-list">
                {(
                  [
                    ["Modality", testCase.modality || "—"],
                    ["Body part", testCase.body_part || "—"],
                    ["Sex", testCase.patient_sex || "—"],
                    ["Age", testCase.patient_age || "—"],
                    ["Images", String(instances.length)],
                    ["Series", String(series.length)],
                    ["Readable live", `${diagnosticCount} of ${instances.length}`],
                  ] as const
                ).map(([k, v]) => (
                  <div key={k} className="rr-kv">
                    <span style={{ color: "#a9b4c2" }}>{k}</span>
                    <span className="rr-mono">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>
        )}
        {dockSide === "right" && mode === "full" && dockColumnEl("right")}
      </div>

      {asking && <LabelSheet initial={asking.initial} onAnswer={answer} />}
    </div>
  );

  // EMBEDDED AND THE CASE HAS NOTHING TO READ LIVE: the old viewer, as it
  // was, with the page's own series pane still driven through `onSeries`.
  // A candidate whose case holds only JPEGs sees exactly what they would
  // have seen before -- no dark frame around it, no note about uploads.
  if (mode === "embedded" && (query.isLoading || stageFallback)) {
    return query.isLoading ? (
      <p className="rr-embed-loading">Loading images…</p>
    ) : (
      <div className="rr-embed-fallback">
        {explainFallback && broken && (
          <p data-cy="rr-fallback-note" className="rr-embed-note">
            The diagnostic viewer could not start, so these are the rendered images. {broken}
          </p>
        )}
        {explainFallback && !broken && (
          <p data-cy="rr-fallback-note" className="rr-embed-note">
            Stored before the diagnostic viewer — showing the rendered images. Re-upload this case to read it live.
          </p>
        )}
        <TestCaseViewer source={source} showSeriesRail={showSeriesRail} {...(onSeries ? { onSeries } : {})} />
      </div>
    );
  }

  return (
    <>
      {mode === "embedded" && <div ref={anchorRef} className="rr-anchor" />}
      {createPortal(room, host)}
    </>
  );
}

/**
 * The text label's question. Enter places it; Escape or Cancel gives it
 * up, which removes a new label and leaves an edited one as it was.
 */
function LabelSheet({ initial, onAnswer }: { initial: string; onAnswer: (text: string | null) => void }) {
  const [text, setText] = useState(initial);
  return (
    <div className="rr-sheet-backdrop" data-cy="rr-label-sheet">
      <form
        className="rr-sheet"
        role="dialog"
        aria-label="Text label"
        onSubmit={(e) => {
          e.preventDefault();
          onAnswer(text.trim() ? text.trim() : null);
        }}
        onKeyDown={(e) => {
          // Stopped HERE, not left to the room's guard: React flushes this
          // answer before the event reaches `window`, so by then the sheet
          // is gone and the same Escape would close the whole room.
          e.stopPropagation();
          if (e.key === "Escape") {
            e.preventDefault();
            onAnswer(null);
          }
        }}
      >
        <label className="rr-sheet-label" htmlFor="rr-label-text">
          {initial ? "Edit the label" : "Label"}
        </label>
        <input
          id="rr-label-text"
          className="rr-sheet-input"
          data-cy="rr-label-input"
          autoFocus
          maxLength={80}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="rr-sheet-actions">
          <button type="button" className="rr-chip-btn" onClick={() => onAnswer(null)}>
            Cancel
          </button>
          <button
            type="submit"
            className="rr-chip-btn rr-chip-btn--accent"
            data-cy="rr-label-ok"
            disabled={!text.trim()}
          >
            Place label
          </button>
        </div>
      </form>
    </div>
  );
}
